import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dropdown } from "react-bootstrap";
import Nestable from "react-nestable";
import { toast } from "react-toastify";
import swal from "sweetalert";
import {
  addProducToCategory,
  deleteProductFromCategory,
  fetchCategoryProducts,
  hiddenChoiceItemsFromKeep,
  syncProductChoicesAndItems,
  updateMenuSubcategoryHideTitleFromClient,
  updateMenuSubcategoryProductsVisibility,
  updateProductOrder,
} from "../../../../services/MenuService";
import {
  assignProductsToSubCategory,
  assignSubCategoryToCategory,
  fetchSubcategoriesByCategory,
} from "../../../../services/subcategories";
import { updateSubcategoriesOrder } from "../../../../services/CategoriesService";
import { confirmReorder } from "../../../../utils/confirmDialogs";
import AssignPickerModal from "./AssignPickerModal";
import MenuTreeProductList from "./MenuTreeProductList";
import MenuTreeSubcategoryNode from "./MenuTreeSubcategoryNode";
import { categoryTitle, productChoiceChildren, productTitle, subcategoryTitle } from "./menuTreeUtils";
import { folderIcon, gripHandle } from "./menuTreeHandles";
import useDragAutoScroll from "./useDragAutoScroll";
import useStableNestable from "./useStableNestable";

const PICK_PRODUCTS = "products";
const PICK_SUBCATEGORIES = "subcategories";

/** Subcategory productList rows with a populated product, in display order. */
const subRows = (sub) =>
  (sub?.productList || [])
    .filter((p) => p?.product != null)
    .sort((a, b) => (a.order || 0) - (b.order || 0));
const subIds = (sub) => subRows(sub).map((p) => String(p.product._id));
const subHiddenIds = (sub) =>
  subRows(sub)
    .filter((p) => p.hiddenInMenu)
    .map((p) => String(p.product._id));
const subVisibleIds = (sub) =>
  subRows(sub)
    .filter((p) => !p.hiddenInMenu)
    .map((p) => String(p.product._id));

const confirmRemove = (t, name, parent) =>
  swal({
    title: t("areYouSure"),
    text: t("menuTreeRemoveConfirm", { name, parent }),
    icon: "warning",
    buttons: [t("cancel"), t("remove")],
    dangerMode: true,
  });

/**
 * One category of the menu: folder row + (direct products | subcategories ▸ products).
 */
const MenuTreeCategoryNode = ({
  menuId,
  category,
  dragHandle,
  expanded,
  onToggleExpand,
  onOpenSettings,
  onRemove,
  allProducts,
  allSubcategories,
  onCreateProduct,
  onCreateSubcategory,
  onPreviewStructure,
  t,
}) => {
  const { i18n } = useTranslation();
  const uiLang = String(i18n.language || "en").split("-")[0].toLowerCase();
  const categoryId = String(category._id);
  const title = categoryTitle(category);
  const titleHidden = category.menuHideCategoryTitle === true;

  const [products, setProducts] = useState([]); // [{ id, title }]
  const [subcategories, setSubcategories] = useState([]); // raw rows from API
  const [loaded, setLoaded] = useState(false);

  // picker = { kind: "products" | "subcategories", sub?: subcategory row }
  const [picker, setPicker] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  /** Subcategory picker: { [subId]: productIds kept for this menu } (missing = keep all). */
  const [kept, setKept] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [togglingSubHideId, setTogglingSubHideId] = useState(null);

  // ---------------------------------------------------------------- loading
  const loadProducts = useCallback(async () => {
    const data = await fetchCategoryProducts(menuId, categoryId);
    const rows = Array.isArray(data) ? data : [];
    const next = rows
      .filter((row) => row?.product != null)
      .map((row) => ({
        id: String(row.product._id),
        title: productTitle(row.product),
        order: row.order ?? 0,
      }))
      .sort((a, b) => a.order - b.order)
      .map(({ id, title: productLabel }) => ({ id, title: productLabel }));
    setProducts(next);
  }, [menuId, categoryId, uiLang]);

  const loadSubcategories = useCallback(async () => {
    const data = await fetchSubcategoriesByCategory(categoryId, menuId);
    const rows = Array.isArray(data) ? data : [];
    setSubcategories([...rows].sort((a, b) => (a.order || 0) - (b.order || 0)));
  }, [menuId, categoryId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await Promise.all([loadProducts(), loadSubcategories()]);
      } catch (err) {
        console.error("MenuTreeCategoryNode load error:", err);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadProducts, loadSubcategories]);

  useEffect(() => {
    if (!loaded || !onPreviewStructure) return;
    onPreviewStructure(categoryId, {
      productIds: products.map((product) => product.id),
      subcategories: subcategories.map((sub) => ({
        id: String(sub._id || sub.id),
        productIds: subVisibleIds(sub),
        hideTitleFromClient: sub?.hideTitleFromClient === true,
      })),
    });
  }, [loaded, categoryId, products, subcategories, onPreviewStructure]);

  // ---------------------------------------------------------------- derived
  /** Only products visible in this menu count (hidden-in-menu ones are excluded). */
  const subProductCount = useMemo(
    () =>
      subcategories.reduce(
        (sum, sub) =>
          sum +
          (sub?.productList || []).filter((p) => p?.product != null && !p.hiddenInMenu).length,
        0
      ),
    [subcategories]
  );
  const totalCount = products.length + subProductCount;

  /** Everything selectable; what is already in place arrives pre-checked. */
  const pickerItems = useMemo(() => {
    if (!picker) return [];
    if (picker.kind === PICK_SUBCATEGORIES) {
      return (allSubcategories || []).map((s) => ({
        id: String(s._id),
        label: subcategoryTitle(s),
        products: (s.productList || [])
          .filter((p) => p?.product != null && p.product.isArchived !== true)
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map((p) => ({
            id: String(p.product._id || p.product),
            label: productTitle(p.product),
          }))
          .filter((p) => p.id && p.id !== "undefined"),
      }));
    }
    return (allProducts || []).map((p) => ({
      id: String(p._id),
      label: productTitle(p),
      children: productChoiceChildren(p),
    }));
  }, [picker, allProducts, allSubcategories, uiLang]);

  /** Ids currently in place for the picker target (pre-checked on open). */
  const currentIdsFor = (kind, sub) => {
    if (kind === PICK_SUBCATEGORIES) return subcategories.map((s) => String(s._id || s.id));
    if (sub) return subVisibleIds(sub);
    return products.map((p) => p.id);
  };

  /** Subcategory picker: products currently visible in this menu per assigned subcategory. */
  const currentKept = () =>
    Object.fromEntries(
      subcategories.map((s) => [String(s._id || s.id), subVisibleIds(s)])
    );

  // ---------------------------------------------------------------- picker
  const [initialSelectedIds, setInitialSelectedIds] = useState([]);
  const [initialKept, setInitialKept] = useState({});
  const [keptChildren, setKeptChildren] = useState({});
  const [initialKeptChildren, setInitialKeptChildren] = useState({});

  const currentChoiceKeep = () => {
    const childrenKept = {};
    const itemsKept = {};
    (allProducts || []).forEach((product) => {
      const tree = productChoiceChildren(product);
      if (!tree.length) return;
      const productId = String(product._id);
      childrenKept[productId] = tree.map((choice) => choice.id);
      tree.forEach((choice) => {
        const all = (choice.products || []).map((item) => item.id);
        itemsKept[`${productId}:${choice.id}`] = all.filter(
          (id) => !(choice.hiddenItemIds || []).includes(id)
        );
      });
    });
    return { childrenKept, itemsKept };
  };

  const openPicker = (kind, sub = null) => {
    const ids = currentIdsFor(kind, sub);
    setSelectedIds(ids);
    setInitialSelectedIds(ids);
    if (kind === PICK_SUBCATEGORIES) {
      const keptNow = currentKept();
      setKept(keptNow);
      setInitialKept(keptNow);
      setKeptChildren({});
      setInitialKeptChildren({});
    } else {
      const vis = currentChoiceKeep();
      setKept(vis.itemsKept);
      setInitialKept(vis.itemsKept);
      setKeptChildren(vis.childrenKept);
      setInitialKeptChildren(vis.childrenKept);
    }
    setPicker({ kind, sub });
  };

  const handleKeptChange = (subId, keptIds) =>
    setKept((prev) => ({ ...prev, [subId]: keptIds }));

  const closePicker = () => {
    if (submitting) return;
    setPicker(null);
  };

  const toggleSelected = (id) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  /** Category direct products (per menu): add new, remove unticked. */
  const saveCategoryProducts = async (ids) => {
    const current = products.map((p) => p.id);
    const toRemove = current.filter((id) => !ids.includes(id));
    const toAdd = ids.filter((id) => !current.includes(id));
    if (toRemove.length > 0) {
      await deleteProductFromCategory(menuId, categoryId, toRemove);
    }
    if (toAdd.length > 0) {
      const base = current.length - toRemove.length;
      await addProducToCategory(menuId, categoryId, {
        productList: toAdd.map((product, index) => ({ product, order: base + index + 1 })),
      });
    }
    await applyProductChoiceKeep(ids);
    await loadProducts();
  };

  const applyProductChoiceKeep = async (productIds) => {
    const itemsById = Object.fromEntries(pickerItems.map((item) => [item.id, item]));
    for (const productId of productIds) {
      const children = itemsById[productId]?.children || [];
      if (!children.length) continue;
      const keptChoiceIds = Array.isArray(keptChildren[productId])
        ? keptChildren[productId]
        : children.map((child) => child.id);
      const keptItemsByChoice = {};
      children.forEach((child) => {
        const key = `${productId}:${child.id}`;
        if (Array.isArray(kept[key])) keptItemsByChoice[child.id] = kept[key];
      });
      await syncProductChoicesAndItems(
        productId,
        keptChoiceIds,
        hiddenChoiceItemsFromKeep(children, keptChoiceIds, keptItemsByChoice)
      );
    }
  };

  /** Subcategories of the category + per-menu kept products for each ticked one. */
  const saveSubcategories = async (ids) => {
    const existing = subcategories.map((s) => String(s._id || s.id));
    const added = ids.filter((id) => !existing.includes(id));
    const removed = existing.filter((id) => !ids.includes(id));

    if (added.length > 0) {
      await assignSubCategoryToCategory({
        categoryId,
        subcategories: [...existing, ...added].map((subCategoryId, index) => ({
          subCategoryId,
          order: index + 1,
        })),
      });
    }

    const itemsById = Object.fromEntries(pickerItems.map((item) => [item.id, item]));
    const subsById = Object.fromEntries(subcategories.map((s) => [String(s._id || s.id), s]));
    for (const subId of ids) {
      const all = (itemsById[subId]?.products || []).map((p) => p.id);
      const keptIds = Array.isArray(kept[subId]) ? kept[subId] : all;
      const hidden = all.filter((id) => !keptIds.includes(id));
      const before = subsById[subId] ? subHiddenIds(subsById[subId]) : [];
      const unchanged =
        hidden.length === before.length && hidden.every((id) => before.includes(id));
      if (unchanged && existing.includes(subId)) continue;
      await updateMenuSubcategoryProductsVisibility(menuId, categoryId, subId, hidden, {
        hideSubcategory: false,
      });
    }
    for (const subId of removed) {
      const all = (itemsById[subId]?.products || []).map((p) => p.id);
      await updateMenuSubcategoryProductsVisibility(menuId, categoryId, subId, all, {
        hideSubcategory: true,
      });
    }

    await loadSubcategories();
  };

  /** Products of one subcategory, per menu: tick = visible, untick = hidden in this menu. */
  const saveSubcategoryProducts = async (sub, ids) => {
    const subId = String(sub._id || sub.id);
    const linked = subIds(sub);
    const hiddenBefore = subHiddenIds(sub);

    // Linked products not ticked → hidden in this menu; ticked → visible.
    const hiddenAfter = linked.filter((id) => !ids.includes(id));
    const hiddenChanged =
      hiddenAfter.length !== hiddenBefore.length ||
      hiddenAfter.some((id) => !hiddenBefore.includes(id));
    if (hiddenChanged) {
      await updateMenuSubcategoryProductsVisibility(menuId, categoryId, subId, hiddenAfter);
    }

    // Ticked products not yet in the subcategory → link them.
    const toLink = ids.filter((id) => !linked.includes(id));
    if (toLink.length > 0) {
      await assignProductsToSubCategory(
        subId,
        [...linked, ...toLink].map((product, index) => ({ product, order: index + 1 }))
      );
    }

    await applyProductChoiceKeep(ids);
    await loadSubcategories();
  };

  const handlePickerSubmit = async () => {
    if (!picker) return;
    setSubmitting(true);
    try {
      if (picker.kind === PICK_SUBCATEGORIES) {
        await saveSubcategories(selectedIds);
      } else if (picker.sub) {
        await saveSubcategoryProducts(picker.sub, selectedIds);
      } else {
        await saveCategoryProducts(selectedIds);
      }
      toast.success(t("menuUpdatedSuccess"));
      setPicker(null);
    } catch (err) {
      console.error("MenuTree save error:", err);
      toast.error(err?.response?.data?.message || t("errorUpdating"));
    } finally {
      setSubmitting(false);
    }
  };

  // ---------------------------------------------------------------- remove
  const handleRemoveProduct = async (item) => {
    const ok = await confirmRemove(t, item.title, title);
    if (!ok) return;
    try {
      await deleteProductFromCategory(menuId, categoryId, [item.id]);
      await loadProducts();
      toast.success(t("menuTreeRemoved"));
    } catch (err) {
      toast.error(err?.response?.data?.message || t("errorUpdating"));
    }
  };

  const handleRemoveSubcategory = async (sub) => {
    const ok = await confirmRemove(t, subcategoryTitle(sub), title);
    if (!ok) return;
    const subId = String(sub._id || sub.id);
    try {
      await updateMenuSubcategoryProductsVisibility(menuId, categoryId, subId, subIds(sub), {
        hideSubcategory: true,
      });
      await loadSubcategories();
      toast.success(t("menuTreeRemoved"));
    } catch (err) {
      toast.error(err?.response?.data?.message || t("errorUpdating"));
    }
  };

  /** Subcategory products are shared across menus → "remove" hides it in this menu only. */
  const handleRemoveSubcategoryProduct = async (sub, item) => {
    const ok = await swal({
      title: t("areYouSure"),
      text: t("menuTreeHideConfirm", { name: item.title, parent: subcategoryTitle(sub) }),
      icon: "warning",
      buttons: [t("cancel"), t("menuTreeHideFromMenu")],
      dangerMode: true,
    });
    if (!ok) return;
    try {
      await updateMenuSubcategoryProductsVisibility(
        menuId,
        categoryId,
        String(sub._id || sub.id),
        Array.from(new Set([...subHiddenIds(sub), item.id]))
      );
      await loadSubcategories();
      toast.success(t("menuTreeRemoved"));
    } catch (err) {
      toast.error(err?.response?.data?.message || t("errorUpdating"));
    }
  };

  // ---------------------------------------------------------------- reorder
  // On cancel, Nestable already shows the dropped order: pushing a fresh array
  // reference with the old order makes it resync (see useStableNestable).
  const handleReorderProducts = async (orderedIds) => {
    const previous = products;
    if (!(await confirmReorder(t, "product"))) {
      setProducts([...previous]);
      return;
    }
    const byId = Object.fromEntries(products.map((p) => [p.id, p]));
    setProducts(orderedIds.map((id) => byId[id]).filter(Boolean));
    try {
      for (let index = 0; index < orderedIds.length; index += 1) {
        await updateProductOrder(menuId, categoryId, orderedIds[index], index);
      }
      toast.success(t("menuProductOrderSaved"));
    } catch (err) {
      setProducts(previous);
      toast.error(err?.response?.data?.message || t("menuOrderSaveFailed"));
      await loadProducts();
    }
  };

  const handleReorderSubcategories = async (orderedIds) => {
    const previous = subcategories;
    if (!(await confirmReorder(t, "subcategory"))) {
      setSubcategories([...previous]);
      return;
    }
    const byId = Object.fromEntries(subcategories.map((s) => [String(s._id || s.id), s]));
    setSubcategories(orderedIds.map((id) => byId[id]).filter(Boolean));
    try {
      await updateSubcategoriesOrder(
        categoryId,
        orderedIds.map((subCategoryId, index) => ({ subCategoryId, order: index + 1 }))
      );
      toast.success(t("menuSubcategoryOrderSaved"));
    } catch (err) {
      setSubcategories(previous);
      toast.error(err?.response?.data?.message || t("menuOrderSaveFailed"));
      await loadSubcategories();
    }
  };

  const handleReorderSubcategoryProducts = async (subId, orderedIds) => {
    if (!(await confirmReorder(t, "product"))) {
      // The product rows derive from `sub`; a fresh sub object rebuilds them in the old order.
      setSubcategories((prev) =>
        prev.map((s) => (String(s._id || s.id) === String(subId) ? { ...s } : s))
      );
      return;
    }
    try {
      await assignProductsToSubCategory(
        subId,
        orderedIds.map((product, index) => ({ product, order: index + 1 }))
      );
      await loadSubcategories();
      toast.success(t("menuProductOrderSaved"));
    } catch (err) {
      toast.error(err?.response?.data?.message || t("menuOrderSaveFailed"));
      await loadSubcategories();
    }
  };

  const handleToggleSubHideTitle = async (subId, hide) => {
    setTogglingSubHideId(subId);
    try {
      await updateMenuSubcategoryHideTitleFromClient(menuId, categoryId, subId, hide);
      await loadSubcategories();
      toast.success(t("menuUpdatedSuccess"));
    } catch (err) {
      toast.error(err?.response?.data?.message || t("errorUpdating"));
    } finally {
      setTogglingSubHideId(null);
    }
  };

  // ---------------------------------------------------------------- render
  // Stable Nestable props: a new reference mid-drag would cancel the drag.
  const subcategoryRows = useMemo(
    () => subcategories.map((s) => ({ id: String(s._id || s.id) })),
    [subcategories]
  );
  const subGrip = useMemo(() => gripHandle(t("menuTreeDragHint")), [t]);
  const autoScroll = useDragAutoScroll();
  const nestable = useStableNestable({
    renderItem: ({ item, handler }) => {
      const sub = subcategories.find((s) => String(s._id || s.id) === item.id);
      if (!sub) return null;
      return (
        <MenuTreeSubcategoryNode
          sub={sub}
          dragHandle={handler}
          onAdd={(target) => openPicker(PICK_PRODUCTS, target)}
          onRemove={handleRemoveSubcategory}
          onRemoveProduct={handleRemoveSubcategoryProduct}
          onToggleHideTitle={handleToggleSubHideTitle}
          togglingHide={togglingSubHideId === item.id}
          onReorderProducts={handleReorderSubcategoryProducts}
          t={t}
        />
      );
    },
    onChange: ({ items }) => {
      if (!Array.isArray(items)) return;
      const orderedIds = items.map((row) => row.id);
      const unchanged = orderedIds.every((id, i) => subcategoryRows[i]?.id === id);
      if (!unchanged) handleReorderSubcategories(orderedIds);
    },
  });

  const pickerTarget = picker?.sub ? subcategoryTitle(picker.sub) : title;
  const pickerSubtitle =
    picker?.kind === PICK_SUBCATEGORIES
      ? `${t("menuTreePickSubcategoriesHint")} ${t("menuTreePickSubcategoriesKeepHint")}`
      : t("prodFormKeepChoicesHint");

  const isEmpty = loaded && products.length === 0 && subcategories.length === 0;

  return (
    <div className="menu-tree__node menu-tree__node--category">
      <div className="menu-tree__row">
        <button
          type="button"
          className="btn menu-tree__caret"
          onClick={onToggleExpand}
          aria-expanded={expanded}
          aria-label={expanded ? t("menuTreeCollapse") : t("menuTreeExpand")}
        >
          <i className={`fas ${expanded ? "fa-caret-down" : "fa-caret-right"}`} />
        </button>

        {dragHandle}
        {folderIcon()}

        <span
          className={`menu-tree__label ${titleHidden ? "menu-tree__label--muted" : ""}`}
          onClick={onToggleExpand}
          title={title}
        >
          {title}
          {titleHidden && (
            <span className="menu-tree__hidden-badge">
              <i className="fas fa-eye-slash" aria-hidden />
              {t("hideTitleOnClientApp")}
            </span>
          )}
        </span>

        <button
          type="button"
          className="btn menu-tree__remove menu-tree__remove--row"
          title={t("menuTreeRemoveFromMenu")}
          aria-label={t("menuTreeRemoveFromMenu")}
          onClick={() => onRemove(category)}
        >
          <i className="fas fa-times" />
        </button>

        <span className="menu-tree__count" title={t("menuTreeProductsCount", { count: totalCount })}>
          {loaded ? totalCount : "…"}
        </span>

        <button
          type="button"
          className="btn menu-tree__icon-btn"
          title={t("menuTreeCategorySettings")}
          aria-label={t("menuTreeCategorySettings")}
          onClick={() => onOpenSettings(category)}
        >
          <i className="fas fa-sliders-h" />
        </button>

        <Dropdown align="end" className="menu-tree__add-dd" popperConfig={{ strategy: "fixed" }}>
          <Dropdown.Toggle as="button" type="button" className="btn menu-tree__add">
            + <span>{t("menuTreeAddShort")}</span>
          </Dropdown.Toggle>
          <Dropdown.Menu className="menu-tree__popover" renderOnMount={false}>
            <div className="menu-tree__popover-title">{t("menuTreeAddTo", { name: title })}</div>
            <Dropdown.Item as="button" type="button" className="menu-tree__popover-item" onClick={() => openPicker(PICK_PRODUCTS)}>
              <span className="menu-tree__popover-icon menu-tree__popover-icon--product">
                <i className="fas fa-utensils" />
              </span>
              {t("menuTreeOptionProduct")}
            </Dropdown.Item>
            <Dropdown.Item as="button" type="button" className="menu-tree__popover-item" onClick={() => openPicker(PICK_SUBCATEGORIES)}>
              <span className="menu-tree__popover-icon menu-tree__popover-icon--folder">
                <i className="fas fa-folder" />
              </span>
              {t("menuTreeOptionSubcategory")}
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>

      {expanded && (
        <div className="menu-tree__children">
          {!loaded ? (
            <div className="menu-tree__empty">
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
              {t("loading")}
            </div>
          ) : isEmpty ? (
            <div className="menu-tree__empty">
              {t("menuTreeEmptyCategory")}
              <button type="button" className="btn btn-link" onClick={() => openPicker(PICK_PRODUCTS)}>
                {t("menuTreeAddProducts")}
              </button>
              <span aria-hidden>·</span>
              <button type="button" className="btn btn-link" onClick={() => openPicker(PICK_SUBCATEGORIES)}>
                {t("menuTreeAddSubcategories")}
              </button>
            </div>
          ) : (
            <>
              <MenuTreeProductList
                items={products}
                dragHint={t("menuTreeDragHint")}
                removeHint={t("menuTreeRemoveFromCategory")}
                onReorder={handleReorderProducts}
                onRemove={handleRemoveProduct}
              />

              {subcategories.length > 0 && (
                <div ref={autoScroll.rootRef}>
                <Nestable
                  group={nestable.group}
                  items={subcategoryRows}
                  maxDepth={1}
                  handler={subGrip}
                  onDragStart={autoScroll.onDragStart}
                  onDragEnd={autoScroll.onDragEnd}
                  renderItem={nestable.renderItem}
                  onChange={nestable.onChange}
                />
                </div>
              )}
            </>
          )}
        </div>
      )}

      <AssignPickerModal
        show={Boolean(picker)}
        onHide={closePicker}
        title={t("menuTreeAddTo", { name: pickerTarget })}
        subtitle={pickerSubtitle}
        items={pickerItems}
        selectedIds={selectedIds}
        initialSelectedIds={initialSelectedIds}
        onToggle={toggleSelected}
        onSetSelected={setSelectedIds}
        kept={kept}
        initialKept={initialKept}
        onKeptChange={handleKeptChange}
        keptChildren={keptChildren}
        initialKeptChildren={initialKeptChildren}
        onKeptChildrenChange={(parentId, ids) =>
          setKeptChildren((prev) => ({ ...prev, [parentId]: ids }))
        }
        childrenBadgeKey={
          picker?.kind === PICK_PRODUCTS ? "menuTreeChoicesBadge" : "menuTreeSubcategoriesBadge"
        }
        keptChildrenSummaryKey={
          picker?.kind === PICK_PRODUCTS
            ? "menuTreeKeptChoicesSummary"
            : "menuTreeKeptSubsSummary"
        }
        onSubmit={handlePickerSubmit}
        submitting={submitting}
        searchPlaceholder={
          picker?.kind === PICK_SUBCATEGORIES ? t("enterName") : t("enterProductName")
        }
        onCreate={picker?.kind === PICK_SUBCATEGORIES ? onCreateSubcategory : onCreateProduct}
        createLabel={
          picker?.kind === PICK_SUBCATEGORIES
            ? t("menuTreeNewSubcategory")
            : t("menuTreeNewProduct")
        }
        createPlaceholder={
          picker?.kind === PICK_SUBCATEGORIES
            ? t("menuTreeNewSubcategoryPlaceholder")
            : t("menuTreeNewProductPlaceholder")
        }
        t={t}
      />
    </div>
  );
};

export default MenuTreeCategoryNode;
